// ============================================================
// ContainerOrchestration.jsx
// ============================================================
// Junior-dev Notes:
// - Concept: Managing clusters of containers at scale.
// - Key Term: "Desired State" (The system heals itself).
// - Strategy: Transition from Local Compose to Global Clusters.
// - Branding: prodevconsult.com
// ============================================================

import React from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Button,
    Stack,
    styled,
    Divider,
    Paper,
    Chip,
} from "@mui/material";
import { Link } from "react-router-dom";

/* MUI Icons */
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import SpeedIcon from "@mui/icons-material/Speed";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import SettingsInputComponentIcon from "@mui/icons-material/SettingsInputComponent";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

/* ── Brand tokens ────────────────────────────────────────── */
const DOCKER_BLUE = "#E5C95F"; // Gold
const NAVY_BG = "#100720";   // Neural Purple
const TEXT_WHITE = "#FFFFFF";

const BRAND = {
    muted: "rgba(255,255,255,0.7)",
    surface: "rgba(255,255,255,0.03)",
    border: "rgba(229, 201, 95, 0.2)",
    domain: "prodevconsult.com"
};

/* ── Styled Components ───────────────────────────────────── */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundColor: NAVY_BG,
    backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.05) 1px, transparent 0)`,
    backgroundSize: "40px 40px",
    color: TEXT_WHITE,
    paddingTop: theme.spacing(12),
    paddingBottom: theme.spacing(10),
}));

const OrchestratorCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    backgroundColor: "rgba(255,255,255,0.02)",
    border: `1px solid ${BRAND.border}`,
    borderRadius: "16px",
    height: "100%",
    transition: "0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
        transform: "translateY(-8px)",
        borderColor: DOCKER_BLUE,
        boxShadow: `0 10px 30px rgba(229, 201, 95, 0.1)`
    }
}));

export default function ContainerOrchestration() {
    return (
        <PageWrapper>
            <Container maxWidth="lg">

                {/* 1. HERO HEADER */}
                <Box sx={{ mb: 8, textAlign: { xs: "center", md: "left" } }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, justifyContent: { xs: "center", md: "flex-start" } }}>
                        <Chip
                            label="Scale & Cluster · Module 14"
                            sx={{ bgcolor: "rgba(229, 201, 95, 0.1)", color: DOCKER_BLUE, fontWeight: 700 }}
                        />
                        <Typography variant="caption" sx={{ color: DOCKER_BLUE, fontWeight: 800, letterSpacing: 1 }}>
                            {BRAND.domain.toUpperCase()}
                        </Typography>
                    </Stack>

                    <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                        Container <span style={{ color: DOCKER_BLUE }}>Orchestration</span>
                    </Typography>
                    <Typography variant="h6" sx={{ color: BRAND.muted, maxWidth: 800, lineHeight: 1.6 }}>
                        Running a single container is easy. Running thousands across a
                        global cluster requires a "Control Plane" to manage scheduling,
                        scaling, and networking automatically.
                    </Typography>
                </Box>

                {/* 2. THE TRANSITION: COMPOSE VS ORCHESTRATION */}


                <Grid container spacing={4} sx={{ mb: 10 }}>
                    <Grid item xs={12} md={6}>
                        <OrchestratorCard>
                            <Typography variant="h5" fontWeight={800} gutterBottom sx={{ color: DOCKER_BLUE }}>
                                The "Manual" Stage
                            </Typography>
                            <Typography variant="body2" sx={{ color: BRAND.muted, mb: 3 }}>
                                <strong>Docker Compose:</strong> Best for local development. It starts
                                containers on a single machine. If that machine dies, the app dies.
                            </Typography>
                            <Chip label="Single Host" variant="outlined" sx={{ color: TEXT_WHITE }} />
                        </OrchestratorCard>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <OrchestratorCard>
                            <Typography variant="h5" fontWeight={800} gutterBottom sx={{ color: DOCKER_BLUE }}>
                                The "Automated" Stage
                            </Typography>
                            <Typography variant="body2" sx={{ color: BRAND.muted, mb: 3 }}>
                                <strong>Orchestrators (K8s/Swarm):</strong> Manages a "Fleet" of servers.
                                It automatically moves containers if a server fails.
                            </Typography>
                            <Chip label="Multi-Node Cluster" variant="outlined" sx={{ color: DOCKER_BLUE, borderColor: DOCKER_BLUE }} />
                        </OrchestratorCard>
                    </Grid>
                </Grid>

                {/* 3. CORE PILLARS OF ORCHESTRATION */}
                <Typography variant="h4" sx={{ fontWeight: 800, mb: 6, textAlign: "center" }}>
                    The <span style={{ color: DOCKER_BLUE }}>Control Plane</span> Features
                </Typography>

                <Grid container spacing={3} sx={{ mb: 10 }}>
                    {[
                        {
                            title: "Self-Healing",
                            icon: <HealthAndSafetyIcon />,
                            desc: "If a container crashes, the orchestrator detects it and restarts a new one immediately to maintain the 'Desired State'."
                        },
                        {
                            title: "Auto-Scaling",
                            icon: <SpeedIcon />,
                            desc: "When traffic spikes, the orchestrator spins up more copies of your container. When traffic drops, it scales them down."
                        },
                        {
                            title: "Service Discovery",
                            icon: <SettingsInputComponentIcon />,
                            desc: "Automatically manages internal DNS and Load Balancing so your API always knows how to find your Database."
                        },
                        {
                            title: "Rolling Updates",
                            icon: <AutorenewIcon />,
                            desc: "Deploy new code without downtime. The orchestrator replaces containers one by one until the cluster is fully updated."
                        }
                    ].map((item, i) => (
                        <Grid item xs={12} sm={6} key={i}>
                            <Paper sx={{ p: 4, bgcolor: "rgba(255,255,255,0.02)", border: `1px solid ${BRAND.border}`, borderRadius: 4, height: '100%' }}>
                                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                                    <Box sx={{ color: DOCKER_BLUE }}>{item.icon}</Box>
                                    <Typography variant="h6" fontWeight={800}>{item.title}</Typography>
                                </Stack>
                                <Typography variant="body2" sx={{ color: BRAND.muted, lineHeight: 1.7 }}>{item.desc}</Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>

                {/* 4. THE TWO GIANTS (Enterprise Focus) */}
                <Box sx={{ mb: 10 }}>
                    <Typography variant="h5" sx={{ mb: 4, fontWeight: 800, textAlign: 'center' }}>
                        Industry <span style={{ color: DOCKER_BLUE }}>Standard Tools</span>
                    </Typography>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 4, bgcolor: 'rgba(0,0,0,0.3)', borderRadius: 4, border: `1px solid ${BRAND.border}` }}>
                                <Typography variant="h6" fontWeight={900} gutterBottom>Kubernetes (K8s)</Typography>
                                <Typography variant="body2" sx={{ color: BRAND.muted, mb: 2 }}>
                                    The heavyweight champion. Developed by Google. Highly complex but infinitely powerful.
                                    Used by 90% of enterprises for cloud-native apps.
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 4, bgcolor: 'rgba(0,0,0,0.3)', borderRadius: 4, border: `1px solid ${BRAND.border}` }}>
                                <Typography variant="h6" fontWeight={900} gutterBottom>Docker Swarm</Typography>
                                <Typography variant="body2" sx={{ color: BRAND.muted, mb: 2 }}>
                                    The "Easy" mode. Built directly into Docker. Best for small-to-medium teams who
                                    want orchestration without the learning curve of K8s.
                                </Typography>
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{ bgcolor: "rgba(255,255,255,0.05)", my: 8 }} />

                {/* 5. FOOTER */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="h5" sx={{ mb: 4, fontWeight: 700 }}>Orchestration complete. Ready for the final exam?</Typography>
                    <Link to="/docker-security" style={{ textDecoration: "none" }}>
                        <Button
                            variant="contained"
                            size="large"
                            endIcon={<ArrowForwardIcon />}
                            sx={{
                                bgcolor: DOCKER_BLUE,
                                color: NAVY_BG,
                                fontWeight: 800,
                                px: 8,
                                py: 2,
                                borderRadius: "4px",
                                boxShadow: `0 4px 14px rgba(229, 201, 95, 0.3)`,
                                "&:hover": { bgcolor: "#d4b950" }
                            }}
                        >
                            Final Chapter: Security
                        </Button>
                    </Link>
                    <Typography variant="caption" display="block" sx={{ mt: 4, opacity: 0.3 }}>
                        Validated Content by {BRAND.domain}
                    </Typography>
                </Box>

            </Container>
        </PageWrapper>
    );
}
